import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../../stores";
import {
  ContentType,
  WhatsappTemplateActionButtonType,
} from "../../../../models";
import styles from "./WhatsappTemplate.module.scss";

const WhatsappApp = ({
  message,
  isMobile,
  contentType,
  meta,
}: {
  message: string;
  isMobile: boolean;
  contentType?: ContentType;
  meta: any;
}) => {
  const { selectedBot } = useStore("bots");

  if (!selectedBot) return null;

  return (
    <form
      style={{ width: isMobile ? "100%" : "35%" }}
      className={clsx("whatsapp-app white", {
        "pl-5": !isMobile,
      })}
    >
      <div className="iphone">
        <div className="bar bar--ios bar--user">
          <h4 className="title mb-0 is-5">{selectedBot.name}</h4>
        </div>
        <div className="bar bar--conversation js-conversation">
          <div
            className="message message--else"
            style={{
              whiteSpace: "break-spaces",
            }}
          >
            <RenderContentType
              message={message}
              contentType={contentType}
              meta={meta}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

const RenderContentType = ({
  message,
  contentType,
  meta,
}: {
  message: string;
  contentType?: ContentType;
  meta: any;
}) => {
  const { selectedBot } = useStore("bots");

  if (!selectedBot) return null;

  if (contentType === "text") {
    return (
      <div>{message || "Hello World, this is a text message from the bot"}</div>
    );
  } else if (contentType === "media") {
    if (meta?.media?.includes(".mp4")) {
      return (
        <div>
          <video controls className="mb-2 has-rounded-corners">
            <source src={meta?.media} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          {message || "Hello World, this is a text message from the bot"}
        </div>
      );
    }
    return (
      <div>
        <img
          className="mb-2 has-rounded-corners"
          src={meta?.media || "/images/product-default.jpeg"}
          alt=""
        />
        {message || "Hello World, this is a text message from the bot"}
      </div>
    );
  } else if (contentType === "quick-reply") {
    return (
      <div>
        {message ||
          "Hello World, this is a text message from the bot. Have you used Edna before?"}
        <div className="is-flex is-flex-direction-column mt-2 is-justify-content-space-between">
          {meta?.actions?.map((action: any, index: number) => (
            <button type="button" className="mb-1 button is-small w-100">
              {action.title || index + 1}
            </button>
          ))}
        </div>
      </div>
    );
  } else if (contentType === "cta") {
    return (
      <div>
        {message || "Hello World, this is a text message from the bot"}
        <div className="is-flex is-flex-direction-column mt-2 is-justify-content-space-between">
          {meta?.actions?.map((action: any, index: number) => (
            <button type="button" className="mb-1 button is-small w-100">
              <span className="icon mr-1">
                <i
                  className={`fa fa-${
                    action.type === "phone-number" ? "phone" : "link"
                  }`}
                ></i>
              </span>
              {action.text || index + 1}
            </button>
          ))}
        </div>
      </div>
    );
  } else if (contentType === "list-picker") {
    return (
      <div>
        {message || "Hello World, this is a text message from the bot"}
        <div className="is-flex is-flex-direction-column mt-2 is-justify-content-space-between">
          <button type="button" className="button is-small w-100">
            <span className="icon mr-1">
              <i className="fa fa-list"></i>
            </span>
            {meta?.button || "List Picker"}
          </button>
        </div>
      </div>
    );
  } else if (contentType === "card") {
    return (
      <div>
        <span>
          {message || "Hello World, this is a text message from the bot"}
        </span>
        <br />
        <span className="has-text-grey">{meta?.subtitle || "Subtitle"}</span>
        {meta?.media && (
          <img
            className="mb-2 has-rounded-corners"
            src={meta?.media || "/images/product-default.jpeg"}
            alt=""
          />
        )}
        {meta?.buttonType === "quick-reply" && (
          <div className="is-flex is-flex-direction-column mt-2 is-justify-content-space-between">
            {meta?.actions?.map((action: any, index: number) => (
              <button type="button" className="mb-1 button is-small w-100">
                {action.title || index + 1}
              </button>
            ))}
          </div>
        )}
        {meta?.buttonType === "call-to-action" && (
          <div className="is-flex is-flex-direction-column mt-2 is-justify-content-space-between">
            {meta?.actions?.map((action: any, index: number) => (
              <button type="button" className="mb-1 button is-small w-100">
                <span className="icon mr-1">
                  <i
                    className={`fa fa-${
                      action.type === "phone-number" ? "phone" : "link"
                    }`}
                  ></i>
                </span>
                {action.text || index + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  } else if (contentType === "catalog") {
    return (
      <div>
        <div className="mb-2 is-flex is-justify-content-space-between">
          <img
            className="has-rounded-corners"
            src={"/images/product-default.jpeg"}
            alt=""
            style={{ width: 120, objectFit: "cover" }}
          />
          <div className="ml-3">
            <span style={{ fontSize: 11 }}>
              View {selectedBot?.name} Catalog on WhatsApp
            </span>
            <br />
            <span style={{ fontSize: 9 }} className="has-text-grey">
              Browse pictures and details of their offerings.
            </span>
          </div>
        </div>
        {message || "Hello World, this is a text message from the bot"}
        <div className="is-flex is-flex-direction-column mt-2 is-justify-content-space-between">
          <button type="button" className="button is-small w-100">
            View catalog
          </button>
        </div>
      </div>
    );
  } else if (contentType === "carousel") {
    const cards = meta?.cards || [];

    return (
      <div>
        <div className="mb-3">{message ?? "Hello Word"}</div>

        <div className={styles.carouselScrollContainer}>
          {cards?.map((card: any, index: number) => (
            <div
              className={styles.carouselCardContainer}
              style={{
                width: "180px",
                marginRight: index === cards.length - 1 ? "5px" : "0",
              }}
              key={index}
            >
              <div className={`${styles.carouselCard} p-2`}>
                <img
                  className="mb-2 has-rounded-corners w-100"
                  src={card.media || "/images/product-default.jpeg"}
                  alt=""
                  style={{ height: "100px", width: "100%", objectFit: "cover" }}
                />
                <div className="p-1">
                  <div className="mb-2" style={{ fontSize: "14px" }}>
                    {card.body || `Card Body ${index + 1}`}
                  </div>

                  {card.buttons?.map((button: any, index: number) => {
                    if (
                      button.type ===
                      WhatsappTemplateActionButtonType.QUICK_REPLY
                    ) {
                      return (
                        <button
                          key={index}
                          type="button"
                          className="mb-1 button is-small w-100"
                        >
                          {button.text || `Button ${index + 1}`}
                        </button>
                      );
                    } else if (
                      button.type === WhatsappTemplateActionButtonType.WEBSITE
                    ) {
                      return (
                        <button
                          key={index}
                          type="button"
                          className="mb-1 button is-small w-100"
                        >
                          <span className="icon mr-1">
                            <i className="fa fa-link"></i>
                          </span>
                          {button.text || `URL ${index + 1}`}
                        </button>
                      );
                    } else if (
                      button.type ===
                      WhatsappTemplateActionButtonType.PHONE_NUMBER
                    ) {
                      return (
                        <button
                          key={index}
                          type="button"
                          className="mb-1 button is-small w-100"
                        >
                          <span className="icon mr-1">
                            <i className="fa fa-phone"></i>
                          </span>
                          {button.text || `Call ${index + 1}`}
                        </button>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export default observer(WhatsappApp);
